import React, { useState, useEffect, useRef } from 'react';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MessageSquare, Send, Search, Plus, MoreVertical, Phone, Video,
  Paperclip, Smile, Check, CheckCheck, Clock, Users
} from 'lucide-react';

export const MessagesPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Demo conversations
    setConversations([
      {
        id: '1',
        type: 'direct',
        participant: { name: 'Prof. Mukendi Jean', role: 'teacher', initials: 'MJ' },
        lastMessage: 'N\'oubliez pas de soumettre votre TP avant vendredi.',
        lastMessageAt: new Date(Date.now() - 3600000).toISOString(),
        unread: 2
      },
      {
        id: '2',
        type: 'group',
        title: 'INFO101 - Groupe d\'étude',
        participants: 15,
        lastMessage: 'Quelqu\'un a compris l\'exercice 3 ?',
        lastMessageAt: new Date(Date.now() - 7200000).toISOString(),
        unread: 5
      },
      {
        id: '3',
        type: 'direct',
        participant: { name: 'Marie Kabongo', role: 'student', initials: 'MK' },
        lastMessage: 'Merci pour les notes !',
        lastMessageAt: new Date(Date.now() - 86400000).toISOString(),
        unread: 0
      },
      {
        id: '4',
        type: 'announcement',
        title: 'Annonces - Faculté des Sciences',
        lastMessage: 'Report des examens de janvier',
        lastMessageAt: new Date(Date.now() - 172800000).toISOString(),
        unread: 1
      },
    ]);
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      // Load messages for selected conversation
      setMessages([
        { id: '1', sender_id: 'other', content: 'Bonjour, avez-vous terminé le TP ?', created_at: new Date(Date.now() - 7200000).toISOString() },
        { id: '2', sender_id: user?.id, content: 'Bonjour Professeur, j\'y travaille encore.', created_at: new Date(Date.now() - 7000000).toISOString() },
        { id: '3', sender_id: 'other', content: 'N\'oubliez pas de soumettre votre TP avant vendredi.', created_at: new Date(Date.now() - 3600000).toISOString() },
      ]);
    }
  }, [selectedConversation, user?.id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const message = {
      id: Date.now().toString(),
      sender_id: user?.id,
      content: newMessage,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');

    try {
      await axios.post(`${API_URL}/messages`, {
        recipient_id: selectedConversation.participant?.id,
        type: selectedConversation.type,
        content: newMessage
      });
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) {
      return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    if (diff < 604800000) {
      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { 
        weekday: 'short' 
      });
    }
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const filteredConversations = conversations.filter(conv => {
    const searchLower = searchQuery.toLowerCase();
    if (conv.type === 'direct') {
      return conv.participant.name.toLowerCase().includes(searchLower);
    }
    return conv.title?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="h-[calc(100vh-180px)] flex gap-4 animate-slide-in" data-testid="messages-page">
      {/* Conversations List */}
      <Card className={`w-full md:w-80 lg:w-96 flex flex-col ${selectedConversation ? 'hidden md:flex' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('messages')}</CardTitle>
            <NewMessageDialog />
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="message-search-input"
            />
          </div>
        </CardHeader>
        <ScrollArea className="flex-1">
          <div className="px-2 pb-2">
            {filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('noMessages')}
              </div>
            ) : (
              filteredConversations.map(conv => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  selected={selectedConversation?.id === conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  formatTime={formatTime}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </Card>

      {/* Chat Area */}
      <Card className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : ''}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="md:hidden"
                  onClick={() => setSelectedConversation(null)}
                >
                  ←
                </Button>
                <Avatar>
                  <AvatarFallback className="bg-[var(--tenant-primary)] text-white">
                    {selectedConversation.type === 'direct' 
                      ? selectedConversation.participant.initials 
                      : selectedConversation.type === 'group' ? 'G' : 'A'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold">
                    {selectedConversation.type === 'direct' 
                      ? selectedConversation.participant.name 
                      : selectedConversation.title}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {selectedConversation.type === 'direct' 
                      ? selectedConversation.participant.role 
                      : `${selectedConversation.participants || 0} ${language === 'fr' ? 'participants' : 'participants'}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon">
                  <Phone className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon">
                  <Video className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        message.sender_id === user?.id
                          ? 'bg-[var(--tenant-primary)] text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-900 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p className={`text-xs mt-1 ${
                        message.sender_id === user?.id ? 'text-blue-200' : 'text-gray-400'
                      }`}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t flex gap-2">
              <Button type="button" variant="ghost" size="icon">
                <Paperclip className="w-4 h-4" />
              </Button>
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('typeMessage')}
                className="flex-1"
                data-testid="message-input"
              />
              <Button type="submit" disabled={!newMessage.trim()} data-testid="send-message-button">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <MessageSquare className="w-12 h-12 mb-4 text-gray-300" />
            <p>{language === 'fr' ? 'Sélectionnez une conversation' : 'Select a conversation'}</p>
          </div>
        )}
      </Card>
    </div>
  );
};

const ConversationItem = ({ conversation, selected, onClick, formatTime }) => {
  const getIcon = () => {
    if (conversation.type === 'group') return Users;
    if (conversation.type === 'announcement') return MessageSquare;
    return null;
  };
  const Icon = getIcon();

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
        selected ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
      data-testid={`conversation-${conversation.id}`}
    >
      <div className="relative">
        <Avatar>
          <AvatarFallback className={`${
            conversation.type === 'direct' ? 'bg-[var(--tenant-primary)]' : 
            conversation.type === 'group' ? 'bg-green-600' : 'bg-orange-500'
          } text-white`}>
            {conversation.type === 'direct' 
              ? conversation.participant.initials 
              : Icon ? <Icon className="w-4 h-4" /> : 'A'}
          </AvatarFallback>
        </Avatar>
        {conversation.unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {conversation.unread}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-medium text-sm truncate">
            {conversation.type === 'direct' 
              ? conversation.participant.name 
              : conversation.title}
          </p>
          <span className="text-xs text-gray-400">
            {formatTime(conversation.lastMessageAt)}
          </span>
        </div>
        <p className="text-sm text-gray-500 truncate">{conversation.lastMessage}</p>
      </div>
    </div>
  );
};

const NewMessageDialog = () => {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" data-testid="new-message-button">
          <Plus className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('newMessage')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{language === 'fr' ? 'Destinataire' : 'Recipient'}</Label>
            <Input placeholder={language === 'fr' ? 'Rechercher un utilisateur...' : 'Search user...'} />
          </div>
          <div>
            <Label>{language === 'fr' ? 'Message' : 'Message'}</Label>
            <Textarea placeholder={t('typeMessage')} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>{t('cancel')}</Button>
            <Button>{t('sendMessage')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessagesPage;
